import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AriaOrb } from "../components/AriaOrb";
import { Youtube, Loader2, PlayCircle, XCircle, CheckCircle2, Clock, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const LEVEL_LABEL = ["Unseen", "Exposed", "Familiar", "Understands", "Can apply", "Can teach"];

function fmtTime(s) {
  if (s == null) return "";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function Learn() {
  const [videos, setVideos] = useState([]);
  const [url, setUrl] = useState("");
  const [course, setCourse] = useState("");
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/youtube"); setVideos(data); } catch (err) { console.error("[ARIA]", err); }
  };
  useEffect(() => { load(); }, []);

  const ingest = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/youtube/ingest", { url, course });
      if (data.status === "error") toast.error(data.error_message || "Could not process that video");
      else toast.success(`Connected: ${data.title}`);
      setUrl(""); setCourse("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to ingest video");
    } finally { setBusy(false); }
  };

  const openVideo = async (v) => {
    setLoadingDetail(true);
    try { const { data } = await api.get(`/youtube/${v.video_id}`); setActive(data); } catch { toast.error("Could not load transcript"); }
    finally { setLoadingDetail(false); }
  };

  if (active) {
    return (
      <div className="h-screen overflow-y-auto p-8">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => setActive(null)} data-testid="back-to-learn" className="flex items-center gap-1.5 text-sm text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] mb-6">
            <ChevronLeft size={14} /> Back to Learn
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Youtube size={18} className="text-[var(--aria-primary)]" />
            <h1 className="font-display text-2xl">{active.title}</h1>
          </div>
          {active.channel && <p className="text-sm text-[var(--aria-text-muted)] mb-6">{active.channel}</p>}

          {active.concepts?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {active.concepts.map(c => <span key={c} className="aria-pill aria-pill-primary text-xs">{c}</span>)}
            </div>
          )}

          {active.chapters?.length > 0 && (
            <div className="aria-card p-5 mb-6">
              <h3 className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold mb-3">Chapters</h3>
              <div className="space-y-1.5">
                {active.chapters.map((c, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm py-1" data-testid={`chapter-${i}`}>
                    <span className="font-mono text-xs text-[var(--aria-primary)] w-14 shrink-0">{fmtTime(c.time)}</span>
                    <span>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="aria-card p-5">
            <h3 className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold mb-3">Transcript</h3>
            <div className="space-y-4 max-h-[50vh] overflow-y-auto">
              {(active.transcript_chunks || []).map((c) => (
                <div key={c.chunk_id} className="text-sm leading-relaxed border-l-2 border-[var(--aria-border)] pl-3" data-testid={`transcript-chunk-${c.chunk_index}`}>
                  <span className="font-mono text-xs text-[var(--aria-primary)] mr-2">{fmtTime(c.start_time)}</span>
                  {c.text}
                </div>
              ))}
              {(!active.transcript_chunks || active.transcript_chunks.length === 0) && (
                <div className="text-sm text-[var(--aria-text-muted)]">No transcript available.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="font-display text-3xl mb-1">Learn</h1>
        <p className="text-sm text-[var(--aria-text-muted)] mb-6">Connect a YouTube lecture — ARIA fetches the real transcript, chunks it by timestamp, and extracts concepts.</p>

        <form onSubmit={ingest} className="aria-card p-5 mb-8 flex flex-wrap gap-3 items-end" data-testid="youtube-ingest-form">
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">YouTube URL</label>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=…"
              data-testid="youtube-url-input"
              className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
          </div>
          <div className="w-48">
            <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Course (optional)</label>
            <input value={course} onChange={e => setCourse(e.target.value)} placeholder="Thermodynamics"
              data-testid="youtube-course-input"
              className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
          </div>
          <button type="submit" disabled={busy} data-testid="youtube-ingest-submit"
            className="btn btn-primary px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Youtube size={14} />}
            {busy ? "Connecting…" : "Connect"}
          </button>
        </form>

        <div className="grid md:grid-cols-2 gap-5">
          {videos.map(v => (
            <div key={v.video_id} onClick={() => v.status === "ready" && openVideo(v)} data-testid={`video-card-${v.video_id}`}
              className={`aria-card p-5 ${v.status === "ready" ? "cursor-pointer aria-magnetic" : ""}`}>
              <div className="flex items-start justify-between mb-3">
                <Youtube size={20} className="text-[var(--aria-primary)]" />
                {v.status === "processing" && <Loader2 size={14} className="animate-spin text-amber-500" />}
                {v.status === "ready" && <CheckCircle2 size={14} className="text-emerald-500" />}
                {v.status === "error" && <XCircle size={14} className="text-red-500" />}
              </div>
              <h3 className="font-display text-lg leading-tight mb-1 line-clamp-2">{v.title}</h3>
              {v.channel && <p className="text-xs text-[var(--aria-text-muted)] mb-3">{v.channel}</p>}
              {v.status === "error" && <p className="text-xs text-red-500 mb-2">{v.error_message}</p>}
              {v.concepts?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {v.concepts.slice(0, 4).map(c => <span key={c} className="aria-pill text-[10px]">{c}</span>)}
                </div>
              )}
              {v.is_demo && <div className="mt-3 text-[10px] uppercase tracking-wider text-[var(--aria-text-faint)] font-mono">Demo data</div>}
            </div>
          ))}
          {videos.length === 0 && (
            <div className="col-span-full text-center py-16 text-[var(--aria-text-muted)]">
              No lectures connected yet — paste a YouTube URL above.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
